import { useState } from 'react';
import { useModal } from '../../../context/ModalContext';
import styles from './CostEstimator.module.css';

const STEPS = [
  {
    id: 'type',
    question: 'What are you building?',
    options: [
      { value: 'web', label: 'Web App', base: 25000 },
      { value: 'mobile', label: 'Mobile App', base: 38000 },
      { value: 'both', label: 'Web + Mobile', base: 56000 },
      { value: 'desktop', label: 'Desktop App', base: 31000 },
    ],
  },
  {
    id: 'scale',
    question: 'What is the project scale?',
    options: [
      { value: 'mvp', label: 'Startup MVP', desc: 'Core features, get to market fast', multiplier: 1 },
      { value: 'smb', label: 'Small Business', desc: 'Full-featured, polished product', multiplier: 1.6 },
      { value: 'enterprise', label: 'Enterprise', desc: 'Complex workflows, integrations, compliance', multiplier: 2.8 },
    ],
  },
  {
    id: 'features',
    question: 'Which features do you need?',
    multi: true,
    options: [
      { value: 'auth', label: 'User authentication', cost: 10000 },
      { value: 'payments', label: 'Payments / billing', cost: 18000 },
      { value: 'admin', label: 'Admin dashboard', cost: 13000 },
      { value: 'ai', label: 'AI / machine learning', cost: 28000 },
      { value: 'maps', label: 'Maps & location', cost: 10000 },
      { value: 'notifications', label: 'Push notifications', cost: 8000 },
      { value: 'analytics', label: 'Analytics & reporting', cost: 13000 },
      { value: 'api', label: 'Third-party API integration', cost: 10000 },
      { value: 'offline', label: 'Offline / sync support', cost: 15000 },
      { value: 'multilang', label: 'Multi-language support', cost: 6000 },
    ],
  },
  {
    id: 'timeline',
    question: 'What is your timeline?',
    options: [
      { value: 'asap', label: 'As soon as possible', multiplier: 1.3 },
      { value: '1to3', label: '1 – 3 months', multiplier: 1.1 },
      { value: '3to6', label: '3 – 6 months', multiplier: 1.0 },
      { value: '6plus', label: '6+ months', multiplier: 0.9 },
    ],
  },
];

function calcRange(answers) {
  const typeOption = STEPS[0].options.find(o => o.value === answers.type);
  const scaleOption = STEPS[1].options.find(o => o.value === answers.scale);
  const timelineOption = STEPS[3].options.find(o => o.value === answers.timeline);

  const base = typeOption?.base ?? 20000;
  const scaleMult = scaleOption?.multiplier ?? 1;
  const timelineMult = timelineOption?.multiplier ?? 1;

  const featureCost = (answers.features ?? []).reduce((sum, fv) => {
    const f = STEPS[2].options.find(o => o.value === fv);
    return sum + (f?.cost ?? 0);
  }, 0);

  const low = Math.round((base + featureCost) * scaleMult * timelineMult);
  const high = Math.round(low * 1.6);

  return { low, high };
}

const fmtZar = n => `R${n.toLocaleString('en-ZA')}`;

const CostEstimator = () => {
  const { openModal } = useModal();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({ type: null, scale: null, features: [], timeline: null });

  const current = STEPS[step];
  const isLast = step === STEPS.length - 1;
  const isDone = step === STEPS.length;

  const canAdvance = current?.multi
    ? answers.features.length > 0
    : answers[current?.id] != null;

  const select = (field, value) => {
    if (field === 'features') {
      setAnswers(prev => {
        const already = prev.features.includes(value);
        return {
          ...prev,
          features: already
            ? prev.features.filter(v => v !== value)
            : [...prev.features, value],
        };
      });
    } else {
      setAnswers(prev => ({ ...prev, [field]: value }));
    }
  };

  const advance = () => {
    if (isLast) setStep(STEPS.length);
    else setStep(s => s + 1);
  };

  const reset = () => {
    setStep(0);
    setAnswers({ type: null, scale: null, features: [], timeline: null });
  };

  if (isDone) {
    const { low, high } = calcRange(answers);
    return (
      <div className={styles.wrap}>
        <div className={styles.result}>
          <p className={styles.resultLabel}>Estimated project cost</p>
          <p className={styles.resultRange}>
            {fmtZar(low)} – {fmtZar(high)}
          </p>
          <p className={styles.resultNote}>
            This rough estimate is based on your selections. Actual costs depend on
            final scope, design complexity, and integrations. An exact quote is always free.
          </p>
          <div className={styles.resultActions}>
            <button className={styles.ctaBtn} onClick={openModal}>
              Get an exact quote →
            </button>
            <button className={styles.resetBtn} onClick={reset}>
              Start over
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.wrap}>
      {/* Progress */}
      <div className={styles.progress}>
        {STEPS.map((s, i) => (
          <div
            key={s.id}
            className={`${styles.progressDot} ${i <= step ? styles.progressDotActive : ''}`}
          />
        ))}
        <span className={styles.progressText}>Step {step + 1} of {STEPS.length}</span>
      </div>

      {/* Question */}
      <h3 className={styles.question}>{current.question}</h3>

      {/* Options */}
      {current.multi ? (
        <div className={styles.checkGrid}>
          {current.options.map(opt => {
            const checked = answers.features.includes(opt.value);
            return (
              <button
                key={opt.value}
                className={`${styles.checkItem} ${checked ? styles.checkItemActive : ''}`}
                onClick={() => select('features', opt.value)}
              >
                <span className={`${styles.checkbox} ${checked ? styles.checkboxChecked : ''}`}>
                  {checked && (
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                    </svg>
                  )}
                </span>
                {opt.label}
              </button>
            );
          })}
        </div>
      ) : (
        <div className={styles.optionGrid}>
          {current.options.map(opt => {
            const selected = answers[current.id] === opt.value;
            return (
              <button
                key={opt.value}
                className={`${styles.option} ${selected ? styles.optionActive : ''}`}
                onClick={() => select(current.id, opt.value)}
              >
                <span className={styles.optionLabel}>{opt.label}</span>
                {opt.desc && <span className={styles.optionDesc}>{opt.desc}</span>}
              </button>
            );
          })}
        </div>
      )}

      {/* Navigation */}
      <div className={styles.nav}>
        {step > 0 && (
          <button className={styles.backBtn} onClick={() => setStep(s => s - 1)}>
            ← Back
          </button>
        )}
        <button
          className={styles.nextBtn}
          onClick={advance}
          disabled={!canAdvance}
        >
          {isLast ? 'Get estimate →' : 'Next →'}
        </button>
      </div>
    </div>
  );
};

export default CostEstimator;
